/**
 * File and folder icons in the shape Windows Explorer uses: a two-tone folder
 * and a page with a folded corner, tinted by file type.
 *
 * Drawn inline rather than pulled from an icon font so they scale with the row
 * and inherit nothing that could go missing.
 */

const TYPE_COLORS: Record<string, string> = {
  image: '#4aa564',
  video: '#8b5cf6',
  audio: '#e0699a',
  archive: '#c58a3a',
  pdf: '#d64545',
  doc: '#3b7fd4',
  sheet: '#2f9e6e',
  code: '#4c8dff',
  text: '#8b93a3',
}

const EXTENSIONS: Record<string, keyof typeof TYPE_COLORS> = {
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image',
  avif: 'image', svg: 'image', heic: 'image', bmp: 'image', tiff: 'image',
  mp4: 'video', mov: 'video', webm: 'video', mkv: 'video', avi: 'video', m4v: 'video',
  mp3: 'audio', m4a: 'audio', flac: 'audio', wav: 'audio', aac: 'audio', ogg: 'audio',
  zip: 'archive', gz: 'archive', tar: 'archive', '7z': 'archive', rar: 'archive', bz2: 'archive',
  pdf: 'pdf',
  doc: 'doc', docx: 'doc', hwp: 'doc', hwpx: 'doc', odt: 'doc', rtf: 'doc',
  xls: 'sheet', xlsx: 'sheet', csv: 'sheet', ods: 'sheet',
  js: 'code', ts: 'code', tsx: 'code', jsx: 'code', json: 'code', xml: 'code',
  html: 'code', css: 'code', py: 'code', sh: 'code', yml: 'code', yaml: 'code',
  txt: 'text', md: 'text', log: 'text',
}

function accentFor(filename: string): string | null {
  const extension = filename.split('.').pop()?.toLowerCase()
  if (!extension || extension === filename.toLowerCase()) return null
  const kind = EXTENSIONS[extension]
  return kind ? TYPE_COLORS[kind]! : null
}

export function FolderIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="icon">
      {/* Back sheet, peeking above the front one. */}
      <path
        d="M2 5.75A1.75 1.75 0 0 1 3.75 4h3.4c.4 0 .78.14 1.08.4l1.4 1.2c.3.26.68.4 1.08.4h5.54A1.75 1.75 0 0 1 18 7.75v.75H2z"
        fill="#d99b2e"
      />
      {/* Front sheet. */}
      <path
        d="M2 8.2h16v6.05A1.75 1.75 0 0 1 16.25 16H3.75A1.75 1.75 0 0 1 2 14.25z"
        fill="#f5bd52"
      />
    </svg>
  )
}

export function FileIcon({ name, size = 20 }: { name: string; size?: number }) {
  const accent = accentFor(name)
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="icon">
      {/* Page with the top-right corner turned down. */}
      <path
        d="M4.5 3.5A1.5 1.5 0 0 1 6 2h5.2L16 6.8v9.7A1.5 1.5 0 0 1 14.5 18h-8.5A1.5 1.5 0 0 1 4.5 16.5z"
        fill="#e9edf4"
      />
      <path d="M11.2 2 16 6.8h-3.3a1.5 1.5 0 0 1-1.5-1.5z" fill="#aab4c5" />
      {accent && <rect x="6.6" y="10.4" width="6.8" height="4.6" rx="1" fill={accent} />}
    </svg>
  )
}
